import { useCallback, useMemo } from 'react'

import Link from 'antd/es/typography/Link'
import { Flex, Text, Tabs, HStack, VStack } from 'govwell-ui'
import { TabItem } from 'govwell-ui/components/Tabs/Tabs'
import { useLocalStorage } from 'usehooks-ts'

import AIPageContainer from 'src/components/AIAssistant/AIPageContainer'
import AITrainingMaterialsTable from 'src/components/AITrainingMaterialsTable/AITrainingMaterialsTable'
import { Badge } from 'src/components/shared/Badge'
import { ExternalLinkProps } from 'src/constants'
import { useGetUnansweredAIChatbotFAQsCountQuery } from 'src/fetch/aiChatbotFAQ'
import CustomAnswersTable from 'src/pages/AIAssistantTrainPage/CustomAnswersTable'
import ScrapeTemplatesTable from 'src/pages/AIAssistantTrainPage/ScrapeTemplatesTable'
import UnansweredQuestionsTable from 'src/pages/AIAssistantTrainPage/UnansweredQuestionsTable'
import { LocalStorageKey } from 'src/types'

enum AIAssistantTrainTabKey {
  AnswerQuestions = 'AnswerQuestions',
  UploadDocuments = 'UploadDocuments',
  ScrapeWebPages = 'ScrapeWebPages',
}

const AIAssistantTrainPage = () => {
  const [selectedTabKey, setSelectedTabKey] = useLocalStorage<AIAssistantTrainTabKey>(
    LocalStorageKey.AIAssistantTrainTabKey,
    AIAssistantTrainTabKey.AnswerQuestions
  )
  const { count } = useGetUnansweredAIChatbotFAQsCountQuery()

  const tabs: TabItem<AIAssistantTrainTabKey>[] = useMemo(
    () => [
      {
        label: (
          <HStack gap={1}>
            Answer Questions
            {count > 0 && <Badge count={count} />}
          </HStack>
        ),
        key: AIAssistantTrainTabKey.AnswerQuestions,
        renderChildren: () => (
          <>
            <VStack gap={6} width="100%" align="start">
              <UnansweredQuestionsTable />
              <CustomAnswersTable />
            </VStack>
          </>
        ),
      },
      {
        label: 'Upload Documents',
        key: AIAssistantTrainTabKey.UploadDocuments,
        renderChildren: () => (
          <VStack gap={6} width="100%" align="start">
            <Text.Body>
              Upload DOCX files or searchable PDF documents to help the Community Assistant find and
              reference information when responding to users.
            </Text.Body>
            <Text.Body>
              These documents will be visible to users as citations in the Community
              Assistant&apos;s responses.
            </Text.Body>
            <Text.Body className="text-gray-600">
              Need to make a PDF searchable?{' '}
              <Link
                href="https://www.adobe.com/acrobat/hub/make-a-pdf-searchable.html"
                {...ExternalLinkProps}
              >
                Learn how with Adobe&apos;s guide
              </Link>
              .
            </Text.Body>
            <AITrainingMaterialsTable vectorStoreType="AIChat" title="Training Materials" />
          </VStack>
        ),
      },
      {
        label: 'Sync External Websites',
        key: AIAssistantTrainTabKey.ScrapeWebPages,
        renderChildren: () => <ScrapeTemplatesTable />,
      },
    ],
    [count]
  )

  const handleSelectedKeyChange = useCallback(
    (key: AIAssistantTrainTabKey) => {
      setSelectedTabKey(key)
    },
    [setSelectedTabKey]
  )

  return (
    <AIPageContainer
      title={`Train Community Assistant${count > 0 ? ` (${count})` : ''}`}
      customTitleDisplay="Train Community Assistant"
    >
      <Flex direction="column" gap={4} align="stretch">
        <Tabs
          onSelectedTabKeyChange={handleSelectedKeyChange}
          selectedTabKey={selectedTabKey}
          tabs={tabs}
        />
        {tabs.find((i) => i.key === selectedTabKey)?.renderChildren?.()}
      </Flex>
    </AIPageContainer>
  )
}

export default AIAssistantTrainPage
